
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { getProductName } from "@/components/history/ProductUtils";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface IncidentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const IncidentDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: IncidentDialogProps) => {
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [explanation, setExplanation] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: stockItems = [] } = useQuery({
    queryKey: ["stock-items"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("stock_items")
        .select(`
          *,
          business_stocks!inner (
            owner_id
          )
        `)
        .eq("business_stocks.owner_id", user.id);

      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Valider les entrées
      if (!selectedProduct || !quantity || !reason) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }

      const product = stockItems.find(item => item.id === selectedProduct);
      if (!product) throw new Error("Produit non trouvé");

      if (parseInt(quantity) > product.quantity) {
        toast.error("La quantité ne peut pas être supérieure au stock disponible");
        return;
      }

      // Uploader les fichiers
      const mediaUrls = [];
      if (selectedFiles) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const fileExt = file.name.split(".").pop();
          const fileName = `${crypto.randomUUID()}.${fileExt}`;
          const filePath = `${user.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("incident_proofs")
            .upload(filePath, file);

          if (uploadError) throw uploadError;
          mediaUrls.push(filePath);
        }
      }

      // Créer l'incident
      const { error } = await supabase
        .from("stock_incidents")
        .insert({
          stock_item_id: selectedProduct,
          owner_id: user.id,
          quantity: parseInt(quantity),
          reason,
          explanation: explanation || null,
          media_urls: mediaUrls,
          created_by: user.id,
        });

      if (error) throw error;

      // Mettre à jour la quantité du stock
      const { error: updateError } = await supabase
        .from("stock_items")
        .update({ quantity: product.quantity - parseInt(quantity) })
        .eq("id", selectedProduct);

      if (updateError) throw updateError;

      onSuccess();
    } catch (error: any) {
      console.error("Erreur lors de la création de l'incident:", error);
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Signaler un incident</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Produit</Label>
            <Select
              value={selectedProduct}
              onValueChange={setSelectedProduct}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un produit" />
              </SelectTrigger>
              <SelectContent>
                {stockItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {getProductName(item)} ({item.quantity} disponible)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Quantité</Label>
            <Input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              min="1"
              max={stockItems.find(item => item.id === selectedProduct)?.quantity}
            />
          </div>

          <div className="space-y-2">
            <Label>Motif</Label>
            <Select
              value={reason}
              onValueChange={setReason}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un motif" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="damaged">Détruit/Endommagé</SelectItem>
                <SelectItem value="repair">En réparation</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Explication</Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Donnez plus de détails sur l'incident..."
            />
          </div>

          <div className="space-y-2">
            <Label>Preuves (Images/Vidéos)</Label>
            <Input
              type="file"
              onChange={(e) => setSelectedFiles(e.target.files)}
              multiple
              accept="image/*,video/*"
            />
            <p className="text-sm text-muted-foreground">
              Vous pouvez sélectionner plusieurs fichiers
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
